import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw, Search, X } from "lucide-react";
import { useId } from "react";
import { cn } from "@/shared/lib/cn";
import { motionTokens, springs } from "@/shared/lib/motion";
import { ExportMenu } from "./ExportMenu";

type TableToolbarProps = {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  /** Nombre de lignes après filtrage, affiché à côté de la recherche (masqué si absent). */
  resultCount?: number;
  pageSize: number;
  pageSizes?: number[];
  onPageSizeChange: (size: number) => void;
  showLabel: string;
  entriesLabel: string;
  onExport?: () => void;
  exportLabel?: string;
  onReset?: () => void;
  resetLabel?: string;
};

const iconButtonClass =
  "inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-border bg-card text-sm font-medium text-foreground shadow-[var(--shadow-sm)] transition-[background-color,border-color,color] duration-150 hover:border-accent hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25";

/**
 * Barre d'outils d'un tableau : recherche unique, nombre de résultats, nombre de lignes par page
 * (sélecteur segmenté), export PDF / Excel et réinitialisation des filtres.
 */
export function TableToolbar({
  search,
  onSearchChange,
  searchPlaceholder,
  resultCount,
  pageSize,
  pageSizes = [10, 25, 50, 100],
  onPageSizeChange,
  showLabel,
  entriesLabel,
  onExport,
  exportLabel,
  onReset,
  resetLabel,
}: TableToolbarProps) {
  const searchId = useId();
  const pillId = useId();

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-4">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
        <div className="relative w-full min-w-[220px] max-w-md flex-1">
          <label htmlFor={searchId} className="sr-only">
            {searchPlaceholder}
          </label>
          <Search size={17} strokeWidth={1.75} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" aria-hidden="true" />
          <input
            id={searchId}
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            className="h-10 w-full rounded-xl border border-border bg-card-muted pl-10 pr-10 text-sm text-foreground outline-none transition-[background-color,border-color,box-shadow] duration-150 placeholder:text-muted focus:border-primary/40 focus:bg-card focus:ring-4 focus:ring-primary/10 [&::-webkit-search-cancel-button]:hidden"
          />
          <AnimatePresence>
            {search ? (
              <motion.button
                type="button"
                onClick={() => onSearchChange("")}
                aria-label="Effacer la recherche"
                className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-lg text-muted transition-colors hover:bg-accent/20 hover:text-foreground"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: motionTokens.duration.fast }}
              >
                <X size={15} aria-hidden="true" />
              </motion.button>
            ) : null}
          </AnimatePresence>
        </div>

        {resultCount !== undefined ? (
          <p className="rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold tabular-nums text-primary" aria-live="polite">
            {resultCount.toLocaleString("fr-FR")} résultat{resultCount > 1 ? "s" : ""}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{showLabel}</span>
          <div className="inline-flex rounded-xl border border-border bg-card-muted p-1" role="group" aria-label={`${showLabel} ${entriesLabel}`}>
            {pageSizes.map((size) => {
              const active = size === pageSize;
              return (
                <button
                  key={size}
                  type="button"
                  onClick={() => onPageSizeChange(size)}
                  aria-pressed={active}
                  className={cn(
                    "relative isolate h-8 min-w-9 rounded-lg px-2.5 text-xs font-semibold tabular-nums transition-colors duration-150",
                    active ? "text-foreground" : "text-muted hover:text-foreground",
                  )}
                >
                  {active ? (
                    // Pastille qui glisse vers la taille choisie.
                    <motion.span
                      layoutId={pillId}
                      className="absolute inset-0 -z-10 rounded-lg bg-card shadow-[var(--shadow-sm)] ring-1 ring-border"
                      transition={springs.snappy}
                    />
                  ) : null}
                  {size}
                </button>
              );
            })}
          </div>
          <span className="hidden sm:inline">{entriesLabel}</span>
        </div>

        {onExport || onReset ? <span className="mx-1 hidden h-6 w-px bg-border sm:block" aria-hidden="true" /> : null}

        {onExport ? <ExportMenu onExport={onExport} label={exportLabel} /> : null}
        {onReset ? (
        <motion.button
          type="button"
          onClick={onReset}
          aria-label={resetLabel}
          title={resetLabel}
          className={cn(iconButtonClass, "w-10")}
          whileTap={{ rotate: -180 }}
          transition={springs.snappy}
        >
          <RotateCcw size={16} strokeWidth={1.75} aria-hidden="true" />
        </motion.button>
        ) : null}
      </div>
    </div>
  );
}
